import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowUpRight, Rocket, Loader2 } from "lucide-react";

/**
 * SetupChecklistWidget
 * ---------------------
 * Shown on the Dashboard until `onboarding_completed=true` AND all 6 steps
 * are done. Auto-hides after the workspace hits 100%. Owner/Admin can
 * re-open it via WorkspaceSettings → "Herstart onboarding".
 */
export default function SetupChecklistWidget() {
    const { t } = useTranslation();
    const { workspace, refreshWorkspace } = useAuth();
    const [status, setStatus] = useState(null);
    const [restarting, setRestarting] = useState(false);
    const [dismissed, setDismissed] = useState(() => localStorage.getItem("jr_setup_dismissed") === "1");

    useEffect(() => {
        api.get("/onboarding/status").then((r) => setStatus(r.data)).catch(() => {});
    }, [workspace?.id]);

    if (!status) return null;
    const doneCount = status.steps_done || 0;
    const totalCount = status.steps_total || 6;

    // Heropent de wizard: un-dismiss + (indien nodig) onboarding_completed terugzetten.
    const resume = async () => {
        setRestarting(true);
        try {
            if (status.onboarding_completed !== false) await api.post("/onboarding/restart");
            await refreshWorkspace();
            localStorage.removeItem("jr_setup_dismissed");
            setDismissed(false);
            window.dispatchEvent(new Event("jr:open-onboarding"));
            toast.success(t("setup.restarted"));
        } catch (e) {
            toast.error(t("setup.restart_failed"));
        } finally {
            setRestarting(false);
        }
    };

    // Fully set up → collapse to a tiny "edit setup" chip (Jeremy's feedback:
    // the big widget must disappear once everything is filled in).
    if (status.completion_pct >= 100) {
        return (
            <div className="flex justify-end">
                <Link to="/app/workspace" data-testid="setup-complete-mini"
                    className="inline-flex items-center gap-1.5 text-[11px] text-[var(--jr-text-3)] hover:text-white border border-white/8 hover:border-white/20 rounded-full px-3 py-1.5 transition-colors">
                    <CheckCircle2 size={11} className="text-[var(--jr-success)]" />
                    {t("setup.complete_mini")}
                    <ArrowUpRight size={10} />
                </Link>
            </div>
        );
    }
    // Weggeklikt → klein "Setup voltooien"-kaartje met voortgang dat terugleidt naar de wizard.
    if (dismissed) {
        return (
            <div className="flex justify-end">
                <button onClick={resume} disabled={restarting} data-testid="setup-mini-resume"
                    className="inline-flex items-center gap-1.5 text-[11px] text-[var(--jr-text-2)] hover:text-white border border-[var(--jr-brand)]/25 hover:border-[var(--jr-brand)]/50 bg-[var(--jr-brand)]/8 rounded-full px-3 py-1.5 transition-colors disabled:opacity-60">
                    {restarting ? <Loader2 className="animate-spin" size={11}/> : <Rocket size={11} className="text-[var(--jr-brand)]"/>}
                    {t("setup.mini_resume", { done: doneCount, total: totalCount })}
                    <ArrowUpRight size={10} />
                </button>
            </div>
        );
    }

    const items = [
        { k: "essence", label: t("setup.step_essence"), href: "/app/tone-of-voice" },
        { k: "sales_tone", label: t("setup.step_sales_tone"), href: "/app/tone-of-voice" },
        { k: "recruiter_tone", label: t("setup.step_recruiter_tone"), href: "/app/tone-of-voice" },
        { k: "icp", label: t("setup.step_icp"), href: "/app/workspace" },
        { k: "integrations", label: t("setup.step_integrations"), href: "/app/integrations" },
        { k: "first_action", label: t("setup.step_first_action"), href: "/app/sales/signals" },
    ];
    const done = status.steps_done || 0;
    const total = status.steps_total || items.length;
    const pct = status.completion_pct || 0;

    return (
        <div data-testid="setup-checklist" className="rounded-xl border border-[var(--jr-brand)]/25 bg-gradient-to-br from-[var(--jr-brand)]/8 to-transparent p-5">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[var(--jr-brand)]/15 grid place-items-center">
                        <Rocket className="text-[var(--jr-brand)]" size={18}/>
                    </div>
                    <div>
                        <div className="font-display text-lg tracking-tight">{t("setup.widget_title")}</div>
                        <div className="text-xs text-[var(--jr-text-2)] mt-0.5">{t("setup.subtitle")}</div>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="font-display text-3xl tracking-tighter text-[var(--jr-brand)]" data-testid="setup-pct">{pct}%</div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{done}/{total}</div>
                </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full bg-[var(--jr-brand)] transition-all duration-500" style={{ width: `${pct}%` }}/>
            </div>
            {/* Checklist */}
            <div className="mt-4 grid sm:grid-cols-2 gap-1.5">
                {items.map((it) => {
                    const isDone = !!status.steps?.[it.k];
                    return (
                        <Link key={it.k} to={it.href} data-testid={`setup-item-${it.k}`}
                            className="group flex items-center gap-2 rounded-lg border border-white/6 bg-[var(--jr-surface)] px-3 py-2 text-xs hover:border-white/16 transition-colors">
                            {isDone
                                ? <CheckCircle2 size={13} className="text-[var(--jr-success)] shrink-0"/>
                                : <Circle size={13} className="text-[var(--jr-text-3)] shrink-0"/>}
                            <span className={`flex-1 ${isDone ? "text-white line-through opacity-70" : "text-white"}`}>{it.label}</span>
                            <ArrowUpRight size={11} className="text-[var(--jr-text-3)] opacity-0 group-hover:opacity-100 transition-opacity"/>
                        </Link>
                    );
                })}
            </div>
            {/* Actions */}
            <div className="mt-4 flex items-center justify-between gap-3">
                {pct < 100 ? (
                    <button onClick={resume} disabled={restarting} data-testid="setup-resume"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                        {restarting ? <Loader2 className="animate-spin" size={12}/> : <Rocket size={12}/>}
                        {t("setup.resume")}
                    </button>
                ) : (
                    <span className="text-xs text-[var(--jr-success)] font-medium">✓ {t("setup.complete_pct", { n: 100 })}</span>
                )}
                <button onClick={() => { localStorage.setItem("jr_setup_dismissed", "1"); setDismissed(true); }}
                    data-testid="setup-dismiss" className="text-[11px] text-[var(--jr-text-3)] hover:text-[var(--jr-text-2)]">
                    {t("setup.later")}
                </button>
            </div>
        </div>
    );
}
